/**
 * Response Validation Utility
 *
 * Validates every API response against its Zod schema, in all environments
 * (per the frontend house rule). Logs violations loudly instead of silently
 * accepting drift, but does not throw by default — a schema mismatch should
 * degrade gracefully in the UI rather than white-screen the app.
 */
import type { ZodType } from "zod";

interface ValidationOptions {
  /** Throw on validation failure instead of logging + passing data through. */
  strict?: boolean;
}

export class SchemaValidationError extends Error {
  public readonly endpoint: string;

  constructor(endpoint: string, detail: string) {
    super(`API schema validation failed for ${endpoint}: ${detail}`);
    this.name = "SchemaValidationError";
    this.endpoint = endpoint;
  }
}

/**
 * Validate API response data against a Zod schema.
 *
 * @param schema Zod schema to validate against
 * @param data Raw response data
 * @param endpoint Endpoint label, used only for error/log context
 */
export function validateResponse<T>(
  schema: ZodType<T>,
  data: unknown,
  endpoint: string,
  options: ValidationOptions = {},
): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    const firstIssue = result.error.issues[0];
    const path = firstIssue?.path.join(".") || "root";
    const detail = `${firstIssue?.message ?? "invalid"} at ${path}`;

    console.error(
      `[API Schema Violation] ${endpoint}: ${detail}`,
      result.error.issues,
    );

    if (options.strict) {
      throw new SchemaValidationError(endpoint, detail);
    }

    // Return the original payload so the UI can still render something —
    // schema drift should be loud in the console/monitoring, not a white screen.
    return data as T;
  }

  return result.data;
}
